import { flow, getParent, getRoot, types, cast } from 'mobx-state-tree';
import { NewsModel } from '../news-model';
import { IToyCommuStore } from '.';
import { IRootStore } from 'store/root';
import { MapleWorldCommuBoardId, MapleWorldTestCommuBoardId, MapleWorldCommuCategory } from 'types/enum';
import { mapleWorldCommunityApi } from 'service/axios-api';
import { INewsModel } from '../news';

let workList: any[] = [];

const MapleWorldStore = types
    .model('MapleWorldStore', {
        listModel: types.array(NewsModel),
    })
    .views((self) => ({
        get parent(): IToyCommuStore {
            return getParent<IToyCommuStore>(self);
        },
        get root(): IRootStore {
            return getRoot<IRootStore>(self);
        },
        get productId(): string {
            return this.parent.productId;
        },
        get locale(): string {
            return this.parent.locale;
        },
        get isTestEnv() {
            return ['test'].includes(window.application.params.environment);
        },
    }))
    .actions((self) => ({
        getBoardIdList() {
            const uniqList = new Set<number>([]);

            const cate = self.isTestEnv ? MapleWorldTestCommuBoardId : MapleWorldCommuBoardId;

            self.parent.categories
                .filter((cate) => cate !== MapleWorldCommuCategory.ALL)
                .forEach((cat) => uniqList.add(cate[cat as MapleWorldCommuCategory] as number));
            return uniqList;
        },
        getHeadlineInfo(boardId: number): { [key: string]: string } {
            switch (boardId) {
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.NOTICE]:
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.MAINTENANCE]:
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.KNOWN_ISSUES]:
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.FEEDBACK_BUG_UPDATES]:
                    return {
                        '3321': MapleWorldCommuCategory.NOTICE,
                        '3322': MapleWorldCommuCategory.MAINTENANCE,
                        '3323': MapleWorldCommuCategory.KNOWN_ISSUES,
                        '3327': MapleWorldCommuCategory.FEEDBACK_BUG_UPDATES,
                    };
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.NOTICE]:
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.MAINTENANCE]:
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.KNOWN_ISSUES]:
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.FEEDBACK_BUG_UPDATES]:
                    return {
                        '3521': MapleWorldCommuCategory.NOTICE,
                        '3522': MapleWorldCommuCategory.MAINTENANCE,
                        '3523': MapleWorldCommuCategory.KNOWN_ISSUES,
                        '3524': MapleWorldCommuCategory.FEEDBACK_BUG_UPDATES,
                    };
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.PATCH_NOTES]:
                    return {
                        '3329': MapleWorldCommuCategory.PATCH_NOTES,
                    };
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.PATCH_NOTES]:
                    return {
                        '3526': MapleWorldCommuCategory.PATCH_NOTES,
                    };
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.ONGOING_EVENTS]:
                case MapleWorldCommuBoardId[MapleWorldCommuCategory.EVENT_WINNERS]:
                    return {
                        '3330': MapleWorldCommuCategory.ONGOING_EVENTS,
                        '3332': MapleWorldCommuCategory.EVENT_WINNERS,
                    };
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.ONGOING_EVENTS]:
                case MapleWorldTestCommuBoardId[MapleWorldCommuCategory.EVENT_WINNERS]:
                    return {
                        '3527': MapleWorldCommuCategory.ONGOING_EVENTS,
                        '3529': MapleWorldCommuCategory.EVENT_WINNERS,
                    };
                default:
                    return {};
            }
        },
        setList(list: any[], boardId: number) {
            const startIdx = workList.findIndex((info) => info.BoardId === boardId);
            const endIdx = workList.lastIndexOf((info: any) => info.BoardId === boardId);
            if (startIdx !== -1 && endIdx !== -1) {
                const copyList = [...workList];
                copyList.splice(startIdx, endIdx - startIdx + 1, ...list);
                workList = copyList;
            } else {
                workList = [...workList.filter((info) => info.BoardId !== boardId), ...list];
            }

            self.listModel = cast([...workList].sort((a, b) => b.CreateDate - a.CreateDate));
        },
    }))
    .actions((self) => ({
        getInitialCategory(): MapleWorldCommuCategory {
            if (self.parent.category === 'All' && self.parent.categories.length === 0) {
                return MapleWorldCommuCategory.ALL;
            }

            return self.parent.category as MapleWorldCommuCategory;
        },
        getCategories() {
            self.parent.setCategory(this.getInitialCategory());
            return Object.values(MapleWorldCommuCategory);
        },
        fetchBoard: flow(function* (boardId: number) {
            const headlineInfo = self.getHeadlineInfo(boardId);
            let url = `/api/v1/board/${boardId}/threadsV2`;
            const res = yield mapleWorldCommunityApi
                .get(url, {
                    params: {
                        paginationType: 'MORE',
                        pageSize: 30,
                    },
                })
                .catch(() => []);

            const result = res.data.threads
                .filter((data: any) => data.headlineId in headlineInfo)
                .map((data: any) => {
                    return {
                        Category: headlineInfo[data.headlineId],
                        Id: data.createDate + Number(data.threadId),
                        ImageThumbnail2: self.parent.isDefaultThumbnail(data.thumbnailImageUrl) ? '' : data.thumbnailImageUrl,
                        Title: data.title,
                        Summary: data.summary,
                        LiveDate: new Date(data.createDate * 1000).toISOString(),
                        CreateDate: data.createDate,
                        ThreadId: data.threadId,
                        BoardId: boardId,
                    };
                });

            self.setList(result, boardId);
        }),
        fetchList() {
            self.getBoardIdList().forEach((boardId) => this.fetchBoard(boardId));
        },
    }))
    .views((self) => ({
        get filtered() {
            const category: string = self.parent.category;
            if (category === MapleWorldCommuCategory.ALL) {
                return self.listModel;
            }
            return self.listModel.filter((x) => x.Category === category);
        },
        get list() {
            return this.filtered;
        },
        get currentNews(): INewsModel {
            return this.filtered[self.parent.currentIndex!];
        },
        get prev(): INewsModel {
            return this.filtered[self.parent.currentIndex! - 1];
        },
        get next(): INewsModel {
            return this.filtered[self.parent.currentIndex! + 1];
        },
    }))
    .actions((self) => ({
        setCurrentIndex(idx: number | undefined) {
            self.parent.setCurrentIndex(idx);
        },
        setCurrent(newsId: number | undefined) {
            const index = self.filtered.findIndex((x) => x.Id === newsId);
            this.setCurrentIndex(index >= 0 ? index : undefined);
        },
    }));

export default MapleWorldStore;
